import type { ReactNode } from "react";

import { Bar, BarChart, CartesianGrid, XAxis, YAxis } from "recharts";

import { Avatar, AvatarFallback } from "@/core/components/ui/avatar";
import { Button } from "@/core/components/ui/button";
import { Card, CardContent, CardHeader } from "@/core/components/ui/card";
import {
  type ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "@/core/components/ui/chart";
import { chartColor } from "@/core/components/ui/chart-colors";
import { NoTranslate } from "@/core/components/ui/no-translate";
import { cn } from "@/core/utils/func/cn";

import { CardOptionsMenu } from "../card-options-menu";

export type FinanceChartDatum = {
  month: string;
  profit: number;
  income: number;
  expense: number;
};

export type FinanceReportItem = {
  icon: ReactNode;
  title: string;
  amount: string;
  iconClassName?: string;
};

export type FinanceCardProps = {
  title: string;
  subTitle: string;
  reportTitle: string;
  reportSubTitle: string;
  chartData: FinanceChartDatum[];
  reportData: FinanceReportItem[];
  labels: {
    profit: string;
    income: string;
    expense: string;
  };
  actionLabel?: string;
  onAction?: () => void;
  menuItems?: string[];
  menuAriaLabel?: string;
  onMenuSelect?: (item: string) => void;
  className?: string;
};

export const FinanceCard = ({
  title,
  subTitle,
  reportTitle,
  reportSubTitle,
  chartData,
  reportData,
  labels,
  actionLabel,
  onAction,
  menuItems,
  menuAriaLabel,
  onMenuSelect,
  className,
}: FinanceCardProps) => {
  const chartConfig = {
    profit: {
      label: labels.profit,
      color: chartColor("accent"),
    },
    income: {
      label: labels.income,
      color: chartColor("brand"),
    },
    expense: {
      label: labels.expense,
      color: chartColor("warning"),
    },
  } satisfies ChartConfig;

  return (
    <Card
      className={cn(
        "relative grid grid-cols-1 gap-x-2 gap-y-4 lg:grid-cols-5",
        className
      )}
    >
      <span
        aria-hidden
        className="pointer-events-none absolute inset-x-0 top-0 h-1 rounded-t-[20px] bg-gradient-accent"
      />
      <div className="flex flex-col gap-10 max-lg:border-b max-lg:pb-6 lg:col-span-3 lg:border-r lg:pr-2">
        <CardHeader className="flex flex-row items-start justify-between">
          <div className="flex flex-col gap-1">
            <span className="text-lg font-semibold">{title}</span>
            <span className="text-sm text-muted-foreground">{subTitle}</span>
          </div>
          <CardOptionsMenu
            items={menuItems}
            ariaLabel={menuAriaLabel}
            onSelect={onMenuSelect}
          />
        </CardHeader>
        <CardContent className="flex-1">
          <ChartContainer
            config={chartConfig}
            className="h-full min-h-[260px] w-full"
          >
            <BarChart
              accessibilityLayer
              data={chartData}
              barSize={12}
              margin={{ left: -30, bottom: -5 }}
            >
              <CartesianGrid
                vertical={false}
                strokeDasharray="4"
                stroke="hsl(var(--border))"
              />
              <XAxis
                dataKey="month"
                tickLine={false}
                tickMargin={10}
                axisLine={false}
                tickFormatter={(value) => String(value).slice(0, 3)}
                tick={{ fill: "hsl(var(--muted-foreground))" }}
              />
              <YAxis
                domain={[0, 50]}
                ticks={[0, 10, 20, 30, 40, 50]}
                tickFormatter={(value) => value}
                tickLine={false}
                tickMargin={8}
                axisLine={false}
                tick={{ fill: "hsl(var(--muted-foreground))" }}
              />
              <ChartTooltip
                cursor={false}
                content={<ChartTooltipContent hideLabel />}
              />
              <Bar dataKey="profit" stackId="a" fill="var(--color-profit)" />
              <Bar dataKey="income" stackId="a" fill="var(--color-income)" />
              <Bar dataKey="expense" stackId="a" fill="var(--color-expense)" />
            </BarChart>
          </ChartContainer>
        </CardContent>
      </div>
      <div className="flex flex-col gap-10 lg:col-span-2">
        <CardHeader className="flex flex-row items-start justify-between">
          <div className="flex flex-col gap-1">
            <span className="text-lg font-semibold">{reportTitle}</span>
            <NoTranslate as="span" className="text-sm text-muted-foreground">
              {reportSubTitle}
            </NoTranslate>
          </div>
          <CardOptionsMenu
            items={menuItems}
            ariaLabel={menuAriaLabel}
            onSelect={onMenuSelect}
          />
        </CardHeader>
        <CardContent className="flex flex-1 flex-col justify-between gap-6 text-base">
          {reportData.map((report, index) => (
            <div key={index} className="flex items-center gap-3">
              <Avatar className="size-9 rounded-sm">
                <AvatarFallback
                  className={cn(
                    "shrink-0 rounded-sm bg-accent/10 text-accent-dark dark:text-accent-light [&>svg]:size-[18px]",
                    report.iconClassName
                  )}
                >
                  {report.icon}
                </AvatarFallback>
              </Avatar>
              <div className="flex flex-col">
                <span>{report.title}</span>
                <NoTranslate as="span" className="text-sm text-muted-foreground">
                  {report.amount}
                </NoTranslate>
              </div>
            </div>
          ))}

          {actionLabel ? (
            <Button size="lg" onClick={onAction}>
              {actionLabel}
            </Button>
          ) : null}
        </CardContent>
      </div>
    </Card>
  );
};
